import React, { FC } from 'react'
import styled from 'styled-components'
import {
  Accordion,
  Box,
  Center,
  Grid,
  Text,
  VideoPlayer,
} from '@lojinha/design-system'
import { testId } from '@lojinha/helpers'
import { beholder } from '@lojinha/beholder'
import { useRouter } from 'next/router'
import { convertCelsiusToAriaLabel } from '../../../helpers/formatPhrase'
import { t } from '../../../dictionary'
import { CONTENTS, PREPARATION_METHODS } from '../preparing-methods/constants'
import { formatStep } from '../../../helpers/formatNumber'

type Props = {
  preparationMethods: Array<keyof typeof PREPARATION_METHODS>
}

const Instructions = styled.div(
  ({ theme }) => `
  grid-column: 1 / 24;
  ${theme.media.above('md')} {
    grid-column: 8 / 12;
  }
`
)

const sendAccordionEvent = (
  preparationMethod: keyof typeof PREPARATION_METHODS,
  url: string
) => (hasOpened: boolean): void => {
  if (hasOpened) {
    beholder.shot('structuredEvent', {
      category: 'navigation',
      action: 'accordion_click',
      label: CONTENTS[preparationMethod].title,
      property: [url],
    })
  }
}

const sendVideoEvent = (
  preparationMethod: keyof typeof PREPARATION_METHODS,
  url: string
) => () => {
  beholder.shot('structuredEvent', {
    category: 'navigation',
    action: 'play_video',
    label: CONTENTS[preparationMethod].videoId,
    property: [url],
  })
}

export const HowToPrepare: FC<Props> = ({ preparationMethods }) => {
  const router = useRouter()

  if (!preparationMethods.length) return null

  return (
    <Box
      borderWidth={0}
      borderColor="gray200"
      borderStyle="solid"
      borderBottomWidth={1}
      {...testId('how-to-prepare')}
      paddingBottom="x48"
    >
      <Center>
        <Grid
          gridTemplateColumns={[
            'repeat(24, 1fr)',
            'repeat(24, 1fr)',
            'repeat(12, 1fr)',
          ]}
        >
          <Grid gridColumn={['2 / 24', '2 / 24', '2 / 12']}>
            {preparationMethods.map((method, i) => (
              <Box
                key={i}
                borderWidth={0}
                borderColor="gray200"
                borderStyle="solid"
                borderBottomWidth={1}
                py={['x32', 'x48']}
              >
                <Accordion
                  headingIcon={CONTENTS[method].icon}
                  heading={CONTENTS[method].title.toUpperCase()}
                  isBold
                  handleChange={sendAccordionEvent(method, router?.asPath)}
                >
                  <Box marginTop={['x32', 'x48']}>
                    <Grid
                      gridTemplateColumns={[
                        'repeat(24, 1fr)',
                        'repeat(24, 1fr)',
                        'repeat(12, 1fr)',
                      ]}
                    >
                      <Grid gridColumn={['1 / 24', '1 / 24', '1 / 7']}>
                        <VideoPlayer
                          title={CONTENTS[method].title}
                          videoId={CONTENTS[method].videoId}
                          onPlay={sendVideoEvent(method, router?.asPath)}
                        />
                      </Grid>
                      <Instructions>
                        <Box py="x24">
                          <Text textTransform="uppercase" isBold isBrand>
                            {t.preparingMethods.instructions}
                          </Text>
                        </Box>
                        {CONTENTS[method].steps.map((step, idx) => (
                          <Box display="flex" marginBottom="x12" key={idx}>
                            <ol>
                              <li aria-label={convertCelsiusToAriaLabel(step)}>
                                {formatStep(idx)}&emsp;{step}
                              </li>
                            </ol>
                          </Box>
                        ))}
                      </Instructions>
                    </Grid>
                  </Box>
                </Accordion>
              </Box>
            ))}
          </Grid>
        </Grid>
      </Center>
    </Box>
  )
}
